import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type {
  InboxItem,
  InboxItemTypeFilter,
  InboxListResult,
  InboxReadStateFilter,
  InboxUnreadCount,
} from '../models/inbox.models';

type JsonRecord = Record<string, unknown>;

@Injectable({ providedIn: 'root' })
export class InboxApiService {
  private readonly http = inject(HttpClient);

  async getInbox(params: {
    page: number;
    pageSize: number;
    readState: InboxReadStateFilter;
    type: InboxItemTypeFilter;
  }): Promise<InboxListResult> {
    let httpParams = new HttpParams()
      .set('page', String(params.page))
      .set('pageSize', String(params.pageSize));

    if (params.readState !== 'all') {
      httpParams = httpParams.set('readState', params.readState);
    }
    if (params.type !== 'all') {
      httpParams = httpParams.set('type', params.type);
    }

    const response = await firstValueFrom(
      this.http.get<unknown>('/api/inbox', { params: httpParams })
    );
    return this.mapInboxList(response, params.page, params.pageSize);
  }

  async getUnreadCount(): Promise<InboxUnreadCount> {
    const response = await firstValueFrom(this.http.get<unknown>('/api/inbox/unread-count'));
    const source = this.asRecord(response);
    return {
      count: this.asNumber(source['count']),
    };
  }

  async markAsRead(id: string): Promise<void> {
    await firstValueFrom(this.http.patch(`/api/inbox/${id}/read`, {}));
  }

  async markAsUnread(id: string): Promise<void> {
    await firstValueFrom(this.http.patch(`/api/inbox/${id}/unread`, {}));
  }

  async markAllAsRead(): Promise<void> {
    await firstValueFrom(this.http.patch('/api/inbox/read-all', {}));
  }

  private mapInboxList(raw: unknown, fallbackPage: number, fallbackPageSize: number): InboxListResult {
    const source = this.asRecord(raw);
    const itemsRaw = Array.isArray(raw)
      ? raw
      : Array.isArray(source['items'])
        ? source['items']
        : Array.isArray(source['data'])
          ? source['data']
          : [];

    return {
      items: itemsRaw.map((entry) => this.mapItem(entry)).filter((item) => !!item.id),
      page: this.asNumber(source['page'], fallbackPage),
      pageSize: this.asNumber(source['pageSize'], fallbackPageSize),
      totalCount: this.asNumber(source['totalCount'], this.asNumber(source['total'])),
    };
  }

  private mapItem(raw: unknown): InboxItem {
    const source = this.asRecord(raw);
    const rawType =
      this.asString(source['type']) ||
      this.asString(source['eventType']) ||
      this.asString(source['sourceEntityType']);

    return {
      id: this.asString(source['id']),
      title: this.asString(source['title']) || this.asString(source['name']),
      description: this.asString(source['description']) || this.asString(source['body']),
      type: this.normalizeType(rawType),
      occurredAt: this.asString(source['occurredAt']) || this.asString(source['createdAt']),
      isRead: this.asBoolean(source['isRead']),
      sourceUrl: this.asNullableString(source['sourceUrl']),
      sourceEntityType: this.asNullableString(source['sourceEntityType']),
      sourceEntityId: this.asNullableString(source['sourceEntityId']),
      metadata: this.asObjectOrNull(source['metadata']),
    };
  }

  private normalizeType(value: string): InboxItemTypeFilter {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return 'alert';
    if (normalized.includes('comment')) return 'comment';
    if (normalized.includes('card')) return 'card';
    if (normalized.includes('form')) return 'form';
    if (normalized.includes('alert')) return 'alert';
    return 'alert';
  }

  private asRecord(value: unknown): JsonRecord {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : {};
  }

  private asString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private asNullableString(value: unknown): string | null {
    const parsed = this.asString(value).trim();
    return parsed ? parsed : null;
  }

  private asBoolean(value: unknown): boolean {
    return typeof value === 'boolean' ? value : false;
  }

  private asNumber(value: unknown, fallback = 0): number {
    return typeof value === 'number' ? value : fallback;
  }

  private asObjectOrNull(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  }
}
